import { ensureHTMLElement, ensureHTMLNodeList } from "../ensure";
import { XHRResponse } from "../request";
import type AsyncListManager from "./list-manager";

export function addPaginationHandler(container: HTMLElement | null, listManager: AsyncListManager) {
  if (!container) return () => {};

  const dom = {
    paginationNextBtn: ensureHTMLElement(container.querySelector(".c-listing__pagination-button-next")),
    paginationPrevBtn: ensureHTMLElement(container.querySelector(".c-listing__pagination-button-prev")),
    paginationBtn: ensureHTMLNodeList(container.querySelectorAll(".c-listing__pagination-button")),
  };

  const unsubscribe = listManager.subscribe("success", ({ response }) => {
    renderPagination(response.data);
  });

  function handleClick(e: Event) {
    e.preventDefault();
    const target = e.currentTarget as HTMLButtonElement;
    const page = target.dataset.page ? parseInt(target.dataset.page) : 1;
    listManager.setOption("page", page);
    listManager.load();
  }

  function renderPagination(data: XHRResponse["data"]) {
    const total = Number(data.total_pages);
    const current = Number(data.page);
    const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
    const safeCurrent = safeTotal > 0 ? Math.min(Math.max(1, current), safeTotal) : 1;
    // Keep list manager page state in sync with effective pagination bounds.
    listManager.setOption("page", safeCurrent);
    const buttons = Array.from(dom.paginationBtn) as HTMLButtonElement[];
    if (buttons.length === 0) return;

    const disableBtn = (btn: HTMLButtonElement) => {
      btn.removeAttribute("data-page");
      btn.textContent = "";
      btn.setAttribute("disabled", "true");
      btn.removeAttribute("aria-current");
      btn.removeAttribute("href");
    };

    // Helper to mark current page by comparing numeric values (robust to types)
    const markCurrent = (btn: HTMLButtonElement, page?: number) => {
      const btnPage = page ?? Number(btn.dataset.page);
      if (Number(btnPage) === safeCurrent) btn.setAttribute("aria-current", "page");
      else btn.removeAttribute("aria-current");
    };

    if (safeTotal <= buttons.length) {
      // straight mapping 1..total (simple case)
      for (let i = 0; i < buttons.length; i++) {
        const btn = buttons[i];
        const page = i + 1;
        if (page <= safeTotal) {
          btn.dataset.page = String(page);
          if (btn instanceof HTMLAnchorElement) {
            btn.setAttribute("href", `?pa=${page}&` + listManager.toQueryString(false));
          }
          btn.textContent = String(page);
          btn.removeAttribute("disabled");
          markCurrent(btn, page);
        } else {
          disableBtn(btn);
        }
      }
    } else {
      // sliding window + last page button
      const windowSize = buttons.length - 1; // reserve last button for `total`
      let start = safeCurrent - Math.floor(windowSize / 2);
      start = Math.max(1, Math.min(start, safeTotal - windowSize));

      for (let i = 0; i < windowSize; i++) {
        const page = start + i;
        const btn = buttons[i];

        btn.dataset.page = String(page);
        // always show the actual page number when it's the current page; otherwise
        // show an ellipsis in the last slot of the window only when there's a gap
        const shouldEllipsis = i === windowSize - 1 && page + 1 < safeTotal && page !== safeCurrent;
        btn.textContent = shouldEllipsis ? "…" : String(page);
        if (btn instanceof HTMLAnchorElement) {
          btn.setAttribute("href", `?pa=${page}&` + listManager.toQueryString(false));
        }
        btn.removeAttribute("disabled");
        markCurrent(btn, page);
      }

      // final button always represents `total`
      const lastBtn = buttons[buttons.length - 1];
      lastBtn.dataset.page = String(safeTotal);
      if (lastBtn instanceof HTMLAnchorElement) {
        lastBtn.setAttribute("href", `?pa=${safeTotal}&` + listManager.toQueryString(false));
      }
      lastBtn.textContent = String(safeTotal);
      lastBtn.removeAttribute("disabled");
      lastBtn.hidden = false;
      markCurrent(lastBtn, safeTotal);
    }

    // Next button
    if (safeTotal > 0 && safeCurrent < safeTotal) {
      dom.paginationNextBtn.dataset.page = String(safeCurrent + 1);
      if (dom.paginationNextBtn instanceof HTMLAnchorElement) {
        dom.paginationNextBtn.setAttribute(
          "href",
          `?pa=${safeCurrent + 1}&` + listManager.toQueryString(false),
        );
      }
      dom.paginationNextBtn.removeAttribute("disabled");
    } else {
      dom.paginationNextBtn.removeAttribute("data-page");
      dom.paginationNextBtn.setAttribute("disabled", "true");
    }

    // Prev button
    if (safeCurrent > 1) {
      dom.paginationPrevBtn.dataset.page = String(safeCurrent - 1);
      if (dom.paginationPrevBtn instanceof HTMLAnchorElement) {
        dom.paginationPrevBtn.setAttribute(
          "href",
          `?pa=${safeCurrent - 1}&` + listManager.toQueryString(false),
        );
      }
      dom.paginationPrevBtn.removeAttribute("disabled");
    } else {
      dom.paginationPrevBtn.removeAttribute("data-page");
      dom.paginationPrevBtn.setAttribute("disabled", "true");
    }
  }

  dom.paginationNextBtn.addEventListener("click", handleClick);
  dom.paginationPrevBtn.addEventListener("click", handleClick);
  dom.paginationBtn.forEach((btn) => btn.addEventListener("click", handleClick));

  return () => {
    unsubscribe();
    dom.paginationNextBtn.removeEventListener("click", handleClick);
    dom.paginationPrevBtn.removeEventListener("click", handleClick);
    dom.paginationBtn.forEach((btn) => btn.removeEventListener("click", handleClick));
  };
}
